import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Form,
  FormField,
  Box,
  SpaceBetween,
  Grid,
  Container,
  ColumnLayout,
  Textarea,
  StatusIndicator,
} from '@cloudscape-design/components';
import { flatMap } from 'lodash';
import {
  taskDraftFormSchema,
  TaskDraftFormData,
} from 'src/validations/taskDraftFormSchema';
import { useMediaUpload } from 'src/hooks/useMediaUpload';
import { useMedia } from 'src/store/media/useMedia';
import { FormInput } from 'src/components/v2/FormInput';
import { FormEmployeePicker } from 'src/components/v2/FormEmployeePicker';
import { FormTextEditor } from 'src/components/v2/FormTextEditor';
import useUserProfile from 'src/hooks/useUserProfile';
import { FileData } from 'src/components/textEditor/TextEditor';
import { EntityType } from 'src/contstant/commonLabels';
import {
  extractSrcValues,
  replaceImagesWithMediaIds,
  replaceMediaIdsWithPresignedUrlsAnyContent,
  replacePresignedUrlsWithMediaIdsAnyContent,
} from 'src/utils/mediaUtils';
import UploadProgressIndicator from 'src/components/UploadProgressIndicator';
import RequiredFieldLabel from 'src/components/RequiredFieldLabel';

const defaultValues: TaskDraftFormData = {
  taskName: '',
  // Blank by design: operators must type a duration explicitly. The Zod
  // schema treats undefined as a required-error, keeping the "no silent
  // 1-day fallback" contract. TaskDraftFormData is typed to allow undefined
  // here so no unsafe cast is needed.
  taskDurationDays: undefined,
  taskDescription: '',
  assignee: '',
  auditorObservation: '',
};

/**
 * Props for the TaskDraftForm component.
 * Readonly fields are displayed as static text and are NOT part of the form state.
 */
export interface TaskDraftFormProps {
  /** Pre-populated editable field values */
  initialData?: TaskDraftFormData;
  /** Called with validated, media-processed form data on submit */
  onSubmit: (data: TaskDraftFormData) => void;
  /** Controls submit button loading state */
  isSubmitting?: boolean;
  /** Custom submit button label (default: "Save Draft") */
  submitButtonText?: string;
  /** Controls form visibility — triggers reset when transitioning to false */
  visible?: boolean;
  /** Parameters needed for media upload */
  mediaUploadParams: {
    programId: string;
    businessUnit: string;
    createdBy: string;
  };
  /** Whether to show the success indicator next to the save button */
  showSuccess?: boolean;
  /** Whether to hide the "(Optional)" label next to the submit button */
  hideOptionalLabel?: boolean;
  /** When true all inputs and the submit button are disabled (e.g. cancelled/completed task) */
  disabled?: boolean;
}

/**
 * A pure presentational form for creating/editing task drafts.
 *
 * Uses Zod + react-hook-form for declarative validation and v2 wrapper components
 * (FormInput, FormTextEditor) for field rendering. Handles media upload internally
 * via useMediaUpload before calling the onSubmit callback.
 *
 * This component contains no Redux, no modal wrapper, and no renderMode branching.
 * The parent (TaskDraftPanel) owns all API mutation logic.
 */
const TaskDraftForm: React.FC<TaskDraftFormProps> = ({
  initialData,
  onSubmit,
  isSubmitting = false,
  submitButtonText = 'Save Draft',
  visible = true,
  mediaUploadParams,
  showSuccess = false,
  hideOptionalLabel = false,
  disabled = false,
}) => {
  const form = useForm({
    resolver: zodResolver(taskDraftFormSchema),
    defaultValues: initialData ?? defaultValues,
  });

  const { getUserProgramId } = useUserProfile();
  const { isUploading, uploadProgress, uploadImagesFromContent } = useMediaUpload();
  const { listMediaByIds, mediaList, listMediaByIdsApiStatus } = useMedia();
  const [fileList, setFileList] = useState<Record<string, FileData[]>>({});
  const [uploadError, setUploadError] = useState<string>('');
  const [hasMediaToLoad, setHasMediaToLoad] = useState(false);

  // Track previous visible state for transition detection
  const prevVisibleRef = useRef(visible);

  // Effect 1: Reset form when visible transitions to false
  useEffect(() => {
    if (prevVisibleRef.current && !visible) {
      form.reset(initialData ?? defaultValues);
      setFileList({});
      setUploadError('');
    }
    prevVisibleRef.current = visible;
  }, [visible, initialData, form]);

  // Effect 2: Sync form with initialData changes and re-resolve media URLs.
  // After a save, initialData updates with UUID-based description but Effect 3
  // won't re-fire (same taskDescription string) and Effect 4 won't re-fire
  // (mediaList reference unchanged). Re-apply presigned URLs from the
  // already-loaded mediaList so images remain visible.
  useEffect(() => {
    if (initialData) {
      form.reset(initialData);
      if (visible && mediaList.length > 0 && initialData.taskDescription) {
        const resolved = replaceMediaIdsWithPresignedUrlsAnyContent(
          initialData.taskDescription,
          mediaList,
        );
        if (resolved !== initialData.taskDescription) {
          form.setValue('taskDescription', resolved);
        }
      }
    }
  }, [initialData, form, visible, mediaList]);

  // Effect 3: Resolve media IDs when visible becomes true
  useEffect(() => {
    if (visible && initialData?.taskDescription) {
      const mediaIds = extractSrcValues(initialData.taskDescription);
      if (mediaIds.length > 0) {
        setHasMediaToLoad(true);
        listMediaByIds({
          programId: mediaUploadParams.programId,
          mediaIds,
          businessUnit: mediaUploadParams.businessUnit,
        });
      } else {
        setHasMediaToLoad(false);
      }
    } else {
      setHasMediaToLoad(false);
    }
  }, [
    visible,
    initialData?.taskDescription,
    listMediaByIds,
    mediaUploadParams.programId,
    mediaUploadParams.businessUnit,
  ]);

  // Effect 4: Replace media IDs with presigned URLs when mediaList updates
  useEffect(() => {
    if (visible && mediaList.length > 0) {
      const currentDescription = form.getValues('taskDescription');
      const resolved = replaceMediaIdsWithPresignedUrlsAnyContent(
        currentDescription,
        mediaList,
      );
      form.setValue('taskDescription', resolved);
    }
  }, [visible, mediaList, form]);

  const handleFormSubmit = useCallback(
    async (data: TaskDraftFormData) => {
      setUploadError('');
      const images = flatMap(fileList) as FileData[];
      let processedDescription = data.taskDescription;

      if (images.length > 0) {
        const mediaIdMap = await uploadImagesFromContent(images, {
          programId: mediaUploadParams.programId,
          businessUnit: mediaUploadParams.businessUnit,
          entityType: EntityType.TASK,
          createdBy: mediaUploadParams.createdBy,
        });

        if (!mediaIdMap) {
          setUploadError('Failed to upload images. Please try again.');
          return;
        }

        processedDescription = replaceImagesWithMediaIds(
          processedDescription,
          mediaIdMap,
        );
      }

      // Reverse any presigned URLs back to media IDs so the backend
      // receives UUIDs, not full URLs (applies when editing an existing draft
      // whose images were resolved for display by Effect 4).
      if (mediaList.length > 0) {
        processedDescription = replacePresignedUrlsWithMediaIdsAnyContent(
          processedDescription,
          mediaList,
        );
      }

      onSubmit({ ...data, taskDescription: processedDescription });
    },
    [fileList, mediaUploadParams, onSubmit, uploadImagesFromContent, mediaList],
  );

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <UploadProgressIndicator
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />
      <Form
        actions={<span />}
        secondaryActions={
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Button
              data-testid="submit-button"
              variant="primary"
              loading={isSubmitting || isUploading}
              disabled={disabled}
              onClick={() => form.handleSubmit(handleFormSubmit)()}
            >
              {submitButtonText}
            </Button>
            <Box color="text-body-secondary" fontSize="body-m">
              {!hideOptionalLabel && '(Optional)'}
            </Box>
            {showSuccess && (
              <StatusIndicator type="success" data-testid="save-success-indicator">
                Task saved successfully
              </StatusIndicator>
            )}
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          {uploadError && (
            <Alert
              data-testid="upload-error-alert"
              type="error"
              dismissible
              onDismiss={() => setUploadError('')}
            >
              {uploadError}
            </Alert>
          )}
          {hasMediaToLoad && listMediaByIdsApiStatus.errorMessage && (
            <Alert type="error" data-testid="media-load-error-alert">
              Error loading media details: {listMediaByIdsApiStatus.errorMessage}
            </Alert>
          )}

          {/* Editable fields — short text inputs in responsive grid */}
          <Grid
            data-testid="editable-fields-grid"
            gridDefinition={[
              { colspan: { default: 12, s: 6 } },
              { colspan: { default: 12, s: 3 } },
              { colspan: { default: 12, s: 3 } },
            ]}
          >
            <FormField
              data-testid="task-name-field"
              label={<RequiredFieldLabel label="Task Name" indicatorColor="inherit" />}
              stretch
              errorText={form.formState.errors.taskName?.message}
            >
              <FormInput
                control={form.control}
                name="taskName"
                placeholder="Enter task name"
                data-testid="task-name-input"
                disabled={disabled}
              />
            </FormField>
            <FormField
              data-testid="task-duration-field"
              label={
                <RequiredFieldLabel
                  label="Task Duration (Days)"
                  indicatorColor="inherit"
                />
              }
              stretch
              errorText={form.formState.errors.taskDurationDays?.message}
            >
              <FormInput
                control={form.control}
                name="taskDurationDays"
                type="number"
                placeholder="e.g. 7"
                data-testid="task-duration-input"
                disabled={disabled}
              />
            </FormField>
            <FormField
              data-testid="assignee-field"
              label={<RequiredFieldLabel label="Assignee" indicatorColor="inherit" />}
              stretch
              errorText={form.formState.errors.assignee?.message}
            >
              <FormEmployeePicker
                control={form.control}
                name="assignee"
                programId={getUserProgramId()}
                placeholder="Search by alias or name"
                data-testid="assignee-input"
                disabled={disabled}
              />
            </FormField>
          </Grid>

          {/* Rich text editors — full width */}
          <FormTextEditor
            control={form.control}
            name="taskDescription"
            label={
              <RequiredFieldLabel label="Task Description" indicatorColor="inherit" />
            }
            showFileUpload={false}
            onChangeMedia={(data) =>
              setFileList((prev) => ({ ...prev, taskDescription: data }))
            }
            data-testid="task-description-editor"
            readOnly={disabled}
          />
          <FormField
            label="Auditor Observation"
            errorText={form.formState.errors.auditorObservation?.message}
            data-testid="auditor-observation-field"
          >
            <Controller
              name="auditorObservation"
              control={form.control}
              render={({ field }) => (
                <Textarea
                  value={field.value || ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  onBlur={field.onBlur}
                  placeholder="Enter auditor observation"
                  data-testid="auditor-observation-input"
                  disabled={disabled}
                />
              )}
            />
          </FormField>
        </SpaceBetween>
      </Form>
    </form>
  );
};

export default TaskDraftForm;
